import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, ChevronRight, Coins, Target, Users, Brain, BarChart3, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import SEOHead from '../../../components/SEO/SEOHead';
import SEOFooter from '../../../components/SEO/SEOFooter';
import { getBrazilianStatus } from '../../../utils/detectBrazil';
import { aixPtBR } from '../../../components/mappings/resource-pages/AIX.pt-br';

const AIX = () => {
  const [activeSection, setActiveSection] = useState('intro');
  const isBrazilian = getBrazilianStatus();
  const t = (key) => isBrazilian && aixPtBR[key] ? aixPtBR[key] : key;

  // Article structured data
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "PickStar AI Exchange: The World's First AI-Driven Prediction Market",
    "description": "Discover the PickStar AI Exchange - the first platform combining AI-driven intelligence with real-time sports prediction markets. Buy StarCoins, generate QuickPicks, and compete in the AI prediction market.",
    "author": {
      "@type": "Organization",
      "name": "PickStar AI",
      "url": "https://pickstarai.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PickStar AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pickstarai.com/logo.png"
      }
    },
    "datePublished": "2025-01-17",
    "dateModified": "2025-01-17",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pickstarai.com/resources/aix"
    },
    "image": "https://pickstarai.com/images/aix-hero.png"
  };

  // FAQ structured data
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I buy StarCoins?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Directly in-app via Pix (Brazil) or Stripe (global). StarCoins load into your account balance in real time."
        }
      },
      {
        "@type": "Question",
        "name": "Do I win real money?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No — pools pay out in StarCoins only. The prize is leaderboard glory and status-driven competition."
        }
      },
      {
        "@type": "Question",
        "name": "What's the difference between AI Index and Crowd Odds?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "AI Index is generated from QuickPicks. Crowd Odds are based on StarCoins wagered by users."
        }
      },
      {
        "@type": "Question",
        "name": "Is this legal?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes — StarCoins are credits for entertainment. There are no cash payouts."
        }
      },
      {
        "@type": "Question",
        "name": "How often do markets update?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "In real time. Every QuickPick and every wager shifts the market instantly."
        }
      }
    ]
  };

  // Software Application structured data
  const softwareStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "PickStar AI Exchange",
    "applicationCategory": "Sports Prediction Market",
    "operatingSystem": "Web, iOS, Android",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "15000",
      "bestRating": "5"
    },
    "featureList": [
      "AI-generated QuickPicks",
      "Real-time AI Confidence Index",
      "StarCoin-powered economy",
      "Live prediction markets",
      "Leaderboard competition",
      "Phase 2 wagering pools",
      "AI vs Crowd analytics",
      "Status-driven rewards"
    ],
    "screenshot": "https://pickstarai.com/images/aix-screenshot.png",
    "softwareVersion": "2.0",
    "datePublished": "2025-01-17",
    "provider": {
      "@type": "Organization",
      "name": "PickStar AI"
    }
  };

  const steps = [
    {
      icon: Coins,
      title: "Buy StarCoins",
      description: [
        "StarCoins are the official currency of the AI Exchange",
        "Purchase instantly through Pix (Brazil) or Stripe (global)",
        "StarCoins load into your account balance in real time",
        "Use them to power everything from AI QuickPicks to leaderboard challenges",
        "Think of StarCoins as your trading capital inside the market"
      ]
    },
    {
      icon: Target,
      title: "Fire AI QuickPicks",
      description: [
        "Go to the /market page",
        "Select any game — for example, Bahia vs Cruzeiro",
        "Hit QuickPick",
        "The AI instantly produces a prediction: the winner, a confidence level, and a reason",
        "Each QuickPick feeds into the AI Consensus Index for that matchup",
        "The more QuickPicks fired, the sharper the AI's line becomes"
      ]
    },
    {
      icon: TrendingUp,
      title: "Watch the AI Market Evolve",
      description: [
        "Every QuickPick recalculates the consensus",
        "Confidence levels adjust dynamically: Low, Medium, High",
        "The Exchange displays AI Consensus % vs Crowd Confidence % side by side",
        "You're not just reading predictions — you're watching a live market evolve in real time"
      ]
    },
    {
      icon: Users,
      title: "Phase 2 — Challenge the AI and the Crowd",
      description: [
        "Users will wager StarCoins into live pools",
        "Pools settle when the game ends: losers' coins are distributed to winners pro-rata",
        "Leaderboards track coins won, ROI %, and streaks",
        "AI is always visible as a benchmark index",
        "Every bet has context: are you siding with the machine, fading it, or following the crowd?"
      ]
    }
  ];

  const comparisons = [
    {
      title: "vs Sportsbooks",
      traditional: "Post odds set by human oddsmakers, adjust slowly, charge fees, built for profit",
      aix: "Updates continuously through AI QuickPicks, no vig or hidden fees, thrives on StarCoins and recognition"
    },
    {
      title: "vs Fantasy Sports",
      traditional: "Focus on building lineups, tracking stats, competing in contests. Great for depth, not real-time",
      aix: "Functions like a live financial market for sports outcomes, predictions update instantly, AI Index as benchmark"
    },
    {
      title: "vs Generic Prediction Platforms",
      traditional: "Depend entirely on user liquidity. Markets can be thin, outcomes delayed, experiences static",
      aix: "Blends AI intelligence + crowd liquidity, every market anchored by AI while evolving with community activity"
    }
  ];

  const differentiators = [
    {
      icon: Brain,
      title: "AI + Crowd, Not Just Odds",
      description: "Sportsbooks give you lines. Traditional prediction platforms give you peer bets. PickStar combines them: AI sets the line, the crowd moves it, and everyone competes against both."
    },
    {
      icon: Coins,
      title: "Play-Money Economy, Real Competition",
      description: "Because StarCoins aren't cash, the Exchange avoids regulatory traps of sportsbooks. Accessible globally while still delivering the competition loop that bettors crave."
    },
    {
      icon: Zap,
      title: "Real-Time Dynamics",
      description: "Markets aren't static. Every QuickPick and every bet instantly shifts the consensus. The AI Exchange feels alive, like watching price action on a stock exchange."
    },
    {
      icon: TrendingUp,
      title: "Category Leadership",
      description: "We're not just another prediction market. We're creating the first AI-powered prediction exchange for sports."
    }
  ];

  const phases = [
    {
      phase: "Phase 1",
      status: "Live Today",
      description: "AI QuickPicks + Consensus Index"
    },
    {
      phase: "Phase 2",
      status: "Coming Soon",
      description: "StarCoin pools + leaderboards"
    },
    {
      phase: "Phase 3",
      status: "In Development",
      description: "Advanced team-building, streak multipliers, and AI vs Human challenges"
    }
  ];

  return (
    <>
      <SEOHead
        title="PickStar AI Exchange: The World's First AI-Driven Prediction Market | PickStar"
        description="Discover the PickStar AI Exchange - where artificial intelligence generates live predictions, user participation shapes the AI Confidence Index, and StarCoins power the prediction economy."
        keywords="AI Exchange, AIX, AI prediction market, sports betting AI, QuickPicks, StarCoins, AI Consensus Index, crowd intelligence, sports predictions, PickStar AIX"
        ogType="article"
        ogImage="/images/aix-hero.png"
        structuredData={[articleStructuredData, faqStructuredData, softwareStructuredData]}
      />

      <div className="min-h-screen bg-black">
        {/* Terminal-style Header */}
        <header className="fixed top-0 z-50 w-full bg-black border-b border-white/5">
          <div className="max-w-screen-xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="flex items-center text-[11px] text-white/50 hover:text-white/80 transition-all uppercase tracking-[0.15em]"
              >
                <ArrowLeft className="w-3 h-3 mr-2" />
                {t("BACK")}
              </Link>
              <div className="flex items-center gap-1.5">
                <span className="w-1 h-1 bg-emerald-500/70 animate-pulse" />
                <span className="text-[9px] text-emerald-500/50 uppercase tracking-[0.2em]">
                  {t("LIVE TODAY")}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section - Terminal Style */}
        <section className="min-h-[60vh] flex items-center justify-center px-8 pt-20 pb-12">
          <div className="text-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <h1 className="text-5xl lg:text-7xl font-thin text-white/95 leading-none">
                <span className="block">{t("PickStar AI Exchange")}</span>
                <span className="block bg-gradient-to-r from-white to-emerald-400/70 bg-clip-text text-transparent mt-2">
                  {t("The World's First AI-Driven Prediction Market")}
                </span>
              </h1>
            </motion.div>
            <p className="text-xs text-white/50 uppercase tracking-[0.2em] mb-8">
              {t("Sports Intelligence, Redefined")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/market"
                className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                         text-[11px] font-medium text-white uppercase tracking-[0.15em]
                         hover:from-emerald-500 hover:to-violet-500 transition-all"
              >
                {t("ENTER THE EXCHANGE")}
                <ChevronRight className="ml-2 w-3 h-3" />
              </Link>
              <Link
                to="/upgrade"
                className="inline-flex items-center justify-center px-6 py-3 border border-violet-500/30
                         text-[11px] text-white/70 uppercase tracking-[0.15em]
                         hover:text-violet-300/80 hover:border-violet-400/50 transition-all"
              >
                <Coins className="mr-2 w-3 h-3" />
                {t("BUY STARCOINS")}
              </Link>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-screen-xl mx-auto px-6">
          {/* Introduction Section */}
          <section className="py-12 border-t border-white/5">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">{t("INTRODUCTION")}</span>
              {t("SPORTS INTELLIGENCE REDEFINED")}
            </h2>
            <div className="border border-white/[0.05] p-6">
              <p className="text-sm text-white/60 mb-6 leading-relaxed">
                {t("Prediction markets have exploded in popularity. From financial events to elections, platforms are creating new ways for people to forecast outcomes. But until now, none have combined the power of AI-driven intelligence with the excitement of real-time sports prediction markets.")}
              </p>
              <p className="text-sm text-white/80 mb-4 font-medium">
                {t("The PickStar AI Exchange is the first platform where:")}
              </p>
              <div className="space-y-3 mb-6 ml-4">
                <div className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-emerald-500/50 mt-2" />
                  <span className="text-sm text-white/60">{t("Artificial intelligence generates live predictions, called QuickPicks")}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-violet-500/50 mt-2" />
                  <span className="text-sm text-white/60">{t("User participation shapes and strengthens the AI Confidence Index in real time")}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-emerald-500/50 mt-2" />
                  <span className="text-sm text-white/60">{t("A StarCoin-powered economy fuels interaction, wagering, and leaderboard competition")}</span>
                </div>
              </div>
              <div className="border-t border-white/[0.05] pt-4">
                <p className="text-sm text-emerald-400/80 font-medium uppercase tracking-[0.15em] text-center">
                  {t("This isn't just betting. It isn't just AI. It's the next-generation AI prediction market — built to make sports intelligence interactive, dynamic, and community-driven.")}
                </p>
              </div>
            </div>
          </section>

          {/* How It Works Section */}
          <section className="py-12 border-t border-white/5">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">{t("HOW IT WORKS")}</span>
              {t("STEP BY STEP GUIDE")}
            </h2>
            <div className="space-y-4">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border border-white/[0.05] hover:border-white/10 p-6 transition-all group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 border border-violet-500/20 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-emerald-500/50" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-emerald-400/80 font-mono text-sm">0{index + 1}</span>
                          <h3 className="text-sm text-white/80 uppercase tracking-[0.15em] font-medium">
                            {t(step.title)}
                          </h3>
                        </div>
                        <div className="space-y-2 ml-6">
                          {step.description.map((item, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                              <span className="w-1 h-1 bg-emerald-500/30 mt-2" />
                              <span className="text-sm text-white/50">{t(item)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* Example Section */}
          <section className="py-12 border-t border-white/5">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">{t("EXAMPLE")}</span>
              {t("REAL-TIME MARKET VIEW")}
            </h2>
            <div className="border border-violet-500/10 p-8 bg-gradient-to-r from-emerald-500/[0.01] to-violet-500/[0.01]">
              <p className="text-sm text-white/80 mb-6 font-mono uppercase tracking-wider">
                {t("Imagine Bahia vs Cruzeiro:")}
              </p>
              <div className="space-y-4">
                <div className="border border-white/[0.05] p-4 group hover:border-white/10 transition-all">
                  <div className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 bg-emerald-500/70 animate-pulse" />
                    <p className="text-sm text-white/70">
                      <span className="text-emerald-400/80 font-mono">{t("3 QuickPicks are fired")}</span>
                      <span className="text-white/50 mx-2">→</span>
                      <span className="text-white/60">{t("AI Consensus: Cruzeiro 67%, Bahia 33%")}</span>
                    </p>
                  </div>
                </div>
                <div className="border border-white/[0.05] p-4 group hover:border-white/10 transition-all">
                  <p className="text-sm text-white/70 mb-3">
                    {t("Users begin wagering StarCoins:")}
                  </p>
                  <div className="ml-6 space-y-2">
                    <div className="flex items-start gap-3">
                      <span className="w-1 h-1 bg-violet-500/50 mt-2" />
                      <span className="text-sm text-white/60 font-mono">{t("750 coins on Cruzeiro")}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-1 h-1 bg-violet-500/50 mt-2" />
                      <span className="text-sm text-white/60 font-mono">{t("250 coins on Bahia")}</span>
                    </div>
                  </div>
                </div>
                <div className="border border-white/[0.05] p-4 group hover:border-white/10 transition-all">
                  <p className="text-sm text-white/70 mb-3">
                    {t("The live display shows:")}
                  </p>
                  <div className="ml-6 space-y-2">
                    <div className="flex items-start gap-3">
                      <span className="w-1 h-1 bg-emerald-500/50 mt-2" />
                      <span className="text-sm text-emerald-400/80 font-mono">{t("AI Index: Cruzeiro 67%")}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-1 h-1 bg-violet-500/50 mt-2" />
                      <span className="text-sm text-violet-400/80 font-mono">{t("Crowd Odds: Cruzeiro 75%")}</span>
                    </div>
                  </div>
                </div>
                <div className="border border-white/[0.05] p-4 group hover:border-white/10 transition-all">
                  <p className="text-sm text-white/70">
                    <span className="text-emerald-400/80 font-medium">{t("If Cruzeiro wins")}</span>
                    <span className="text-white/50 mx-2">→</span>
                    <span className="text-white/60">{t("Cruzeiro backers split the 1,000-coin pool based on their share of the stake")}</span>
                  </p>
                </div>
              </div>
              <div className="border-t border-white/[0.05] mt-6 pt-6">
                <p className="text-sm text-emerald-400/80 font-medium uppercase tracking-[0.15em] text-center">
                  {t("Every game has two stories: What the AI said. What the crowd did.")}
                </p>
              </div>
            </div>
          </section>

          {/* Leaderboards Section */}
          <section className="py-12 border-t border-white/5">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">{t("LEADERBOARDS")}</span>
              {t("THE PRIZE IS STATUS")}
            </h2>
            <div className="border border-white/[0.05] p-6">
              <p className="text-sm text-white/60 mb-6">
                {t("PickStar's AI Exchange doesn't pay cash. Instead, it creates status-driven competition.")}
              </p>
              <p className="text-sm text-white/80 mb-4 font-medium">
                {t("Weekly and monthly leaderboards rank players by:")}
              </p>
              <div className="space-y-3 mb-6 ml-4">
                <div className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-emerald-500/50 mt-2" />
                  <span className="text-sm text-white/60">
                    <span className="text-emerald-400/80 font-mono font-medium">{t("Coins Won")}</span>
                    <span className="text-white/40 ml-2">{t("(volume leaders)")}</span>
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-violet-500/50 mt-2" />
                  <span className="text-sm text-white/60">
                    <span className="text-violet-400/80 font-mono font-medium">{t("ROI %")}</span>
                    <span className="text-white/40 ml-2">{t("(efficiency leaders)")}</span>
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-emerald-500/50 mt-2" />
                  <span className="text-sm text-white/60">
                    <span className="text-emerald-400/80 font-mono font-medium">{t("Streaks")}</span>
                    <span className="text-white/40 ml-2">{t("(most consecutive wins)")}</span>
                  </span>
                </div>
              </div>
              <p className="text-sm text-white/80 mb-4">
                {t("Badges and levels celebrate milestones:")}
              </p>
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="px-3 py-1 border border-emerald-500/20 text-xs text-emerald-400/80 font-mono uppercase tracking-wider">{t("Top 1% AI Beater")}</span>
                <span className="px-3 py-1 border border-violet-500/20 text-xs text-violet-400/80 font-mono uppercase tracking-wider">{t("Crowd Crusher")}</span>
                <span className="px-3 py-1 border border-emerald-500/20 text-xs text-emerald-400/80 font-mono uppercase tracking-wider">{t("Hot Hand")}</span>
              </div>
              <div className="border-t border-white/[0.05] pt-4">
                <p className="text-sm text-white/70 font-medium uppercase tracking-[0.15em] text-center">
                  {t("This transforms the AI Exchange into more than a game. It's a social prediction arena — a place where sports fans build identity and bragging rights.")}
                </p>
              </div>
            </div>
          </section>

          {/* Comparison Section */}
          <section className="py-12 border-t border-white/5">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">{t("COMPARISON")}</span>
              {t("HOW WE STAND APART")}
            </h2>
            <div className="space-y-4">
              {comparisons.map((comp, index) => (
                <motion.div
                  key={comp.title}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <h3 className="text-sm text-white/80 mb-4 uppercase tracking-[0.15em] font-medium">{t(comp.title)}</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border border-white/[0.03] p-4">
                      <p className="text-[11px] font-medium text-white/40 mb-3 uppercase tracking-[0.2em]">{t("Traditional")}</p>
                      <p className="text-sm text-white/50">{t(comp.traditional)}</p>
                    </div>
                    <div className="border border-violet-500/10 p-4 bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02]">
                      <p className="text-[11px] font-medium text-violet-400/60 mb-3 uppercase tracking-[0.2em]">{t("PickStar AIX")}</p>
                      <p className="text-sm text-white/60">{t(comp.aix)}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-6 p-6 border border-emerald-500/10 bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02]">
              <p className="text-sm text-white/80 font-medium mb-3">{t("In short:")}</p>
              <div className="space-y-2 ml-4">
                <div className="flex items-start gap-3">
                  <span className="w-1 h-1 bg-white/30 mt-2" />
                  <span className="text-sm text-white/50">{t("Sportsbooks sell odds")}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-1 h-1 bg-white/30 mt-2" />
                  <span className="text-sm text-white/50">{t("Fantasy sells stats")}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-1 h-1 bg-white/30 mt-2" />
                  <span className="text-sm text-white/50">{t("Prediction platforms sell speculation")}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-emerald-500/70 mt-2" />
                  <span className="text-sm text-emerald-400/80 font-medium uppercase tracking-wider">{t("PickStar sells intelligence")}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Why Different Section */}
          <section className="py-12 border-t border-white/5">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">{t("WHY DIFFERENT")}</span>
              {t("Why PickStar AI Exchange Is Different")}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {differentiators.map((diff, index) => {
                const Icon = diff.icon;
                return (
                  <motion.div
                    key={diff.title}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border border-white/[0.05] hover:border-white/10 p-6 transition-all group"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <Icon className="w-5 h-5 text-emerald-500/50" />
                      <h3 className="text-sm font-medium text-white/80 uppercase tracking-[0.15em]">{t(diff.title)}</h3>
                    </div>
                    <p className="text-sm text-white/60 leading-relaxed">{t(diff.description)}</p>
                  </motion.div>
                );
              })}
            </div>
          </section>

          {/* Future Section */}
          <section className="py-12 border-t border-white/5">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">{t("THE FUTURE")}</span>
              {t("AI PREDICTION MARKETS")}
            </h2>
            <p className="text-sm text-white/60 mb-6">
              {t("The PickStar Exchange is just getting started.")}
            </p>
            <div className="space-y-4">
              {phases.map((phase, index) => (
                <motion.div
                  key={phase.phase}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 border border-white/[0.05] hover:border-white/10 p-4 transition-all"
                >
                  <div className="flex-shrink-0">
                    <span className={`inline-block px-3 py-1 text-[11px] font-medium uppercase tracking-wider ${
                      phase.status === 'Live Today'
                        ? 'bg-emerald-500/10 text-emerald-400/80 border border-emerald-500/20'
                        : phase.status === 'Coming Soon'
                        ? 'bg-violet-500/10 text-violet-400/80 border border-violet-500/20'
                        : 'bg-white/5 text-white/30 border border-white/10'
                    }`}>
                      {t(phase.status)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-white/80 font-mono font-medium">{t(phase.phase)}</p>
                    <p className="text-sm text-white/50 mt-1">{t(phase.description)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="border-t border-white/[0.05] mt-8 pt-6">
              <p className="text-sm text-white/70">
                {t("As we scale, the Exchange will become the")} <span className="text-emerald-400/80 font-medium">{t("Bloomberg Terminal of sports prediction markets")}</span> {t("— the place where AI, crowd intelligence, and sports culture collide.")}
              </p>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-8 border-t border-white/5">
            <h2 className="text-2xl font-light text-white/95 mb-8">
              <span className="text-xs text-white/30 uppercase tracking-[0.3em] block mb-2">{t("FAQ")}</span>
              {t("HOW TO USE")}
            </h2>
            <div className="space-y-0 border border-white/[0.05]">
              <details className="group">
                <summary className="px-6 py-4 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05]">
                  <span className="text-sm text-white/80 font-medium">{t("How do I buy StarCoins?")}</span>
                  <ChevronRight className="w-4 h-4 text-white/40 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-6 py-4 text-sm text-white/60 leading-relaxed border-b border-white/[0.05]">
                  {t("Directly in-app via Pix (Brazil) or Stripe (global).")}
                </div>
              </details>

              <details className="group">
                <summary className="px-6 py-4 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05]">
                  <span className="text-sm text-white/80 font-medium">{t("Do I win real money?")}</span>
                  <ChevronRight className="w-4 h-4 text-white/40 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-6 py-4 text-sm text-white/60 leading-relaxed border-b border-white/[0.05]">
                  {t("No — pools pay out in StarCoins only. The prize is leaderboard glory.")}
                </div>
              </details>

              <details className="group">
                <summary className="px-6 py-4 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05]">
                  <span className="text-sm text-white/80 font-medium">{t("What's the difference between AI Index and Crowd Odds?")}</span>
                  <ChevronRight className="w-4 h-4 text-white/40 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-6 py-4 text-sm text-white/60 leading-relaxed border-b border-white/[0.05]">
                  {t("AI Index is generated from QuickPicks. Crowd Odds are based on StarCoins wagered by users.")}
                </div>
              </details>

              <details className="group">
                <summary className="px-6 py-4 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05]">
                  <span className="text-sm text-white/80 font-medium">{t("Is this legal?")}</span>
                  <ChevronRight className="w-4 h-4 text-white/40 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-6 py-4 text-sm text-white/60 leading-relaxed border-b border-white/[0.05]">
                  {t("Yes — StarCoins are credits for entertainment. There are no cash payouts.")}
                </div>
              </details>

              <details className="group">
                <summary className="px-6 py-4 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors">
                  <span className="text-sm text-white/80 font-medium">{t("How often do markets update?")}</span>
                  <ChevronRight className="w-4 h-4 text-white/40 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-6 py-4 text-sm text-white/60 leading-relaxed">
                  {t("In real time. Every QuickPick and every wager shifts the market instantly.")}
                </div>
              </details>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-12 border-t border-white/5 text-center">
            <h2 className="text-2xl font-light text-white/95 mb-8">
              <span className="text-xs text-white/30 uppercase tracking-[0.3em] block mb-2">{t("GET STARTED")}</span>
              {t("START NOW")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              <div className="border border-white/[0.05] hover:border-white/10 p-6 transition-all">
                <p className="text-sm font-medium text-white/80 uppercase tracking-[0.15em] mb-2">{t("Buy StarCoins")}</p>
                <p className="text-sm text-white/50">{t("Instantly load credits into your account")}</p>
              </div>
              <div className="border border-white/[0.05] hover:border-white/10 p-6 transition-all">
                <p className="text-sm font-medium text-white/80 uppercase tracking-[0.15em] mb-2">{t("Fire QuickPicks")}</p>
                <p className="text-sm text-white/50">{t("Generate predictions and shape the AI Index")}</p>
              </div>
              <div className="border border-white/[0.05] hover:border-white/10 p-6 transition-all">
                <p className="text-sm font-medium text-white/80 uppercase tracking-[0.15em] mb-2">{t("Watch the Market")}</p>
                <p className="text-sm text-white/50">{t("See AI % and Crowd Odds diverge in real time")}</p>
              </div>
              <div className="border border-white/[0.05] hover:border-white/10 p-6 transition-all">
                <p className="text-sm font-medium text-white/80 uppercase tracking-[0.15em] mb-2">{t("Prepare for Phase 2")}</p>
                <p className="text-sm text-white/50">{t("Challenge AI + peers in StarCoin pools")}</p>
              </div>
            </div>
            <p className="text-sm text-white/70 font-medium mb-4 uppercase tracking-[0.15em]">
              {t("The PickStar AI Exchange is here. It's sports intelligence redefined.")}
            </p>
            <p className="text-lg text-emerald-400/80 font-medium mb-10 uppercase tracking-[0.2em]">
              {t("Don't just watch the market. Become the market.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/market"
                className="inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                         text-sm font-medium text-white uppercase tracking-[0.15em]
                         hover:from-emerald-500 hover:to-violet-500 transition-all"
              >
                {t("ENTER THE AI EXCHANGE")}
                <ChevronRight className="ml-2 w-4 h-4" />
              </Link>
              <Link
                to="/upgrade"
                className="inline-flex items-center justify-center px-8 py-4 border border-violet-500/30
                         text-sm text-white/70 uppercase tracking-[0.15em]
                         hover:text-violet-300/80 hover:border-violet-400/50 transition-all"
              >
                <Coins className="mr-2 w-4 h-4" />
                {t("BUY STARCOINS NOW")}
              </Link>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-white/5">
          <SEOFooter language={isBrazilian ? 'pt-BR' : 'en'} />
        </div>
      </div>
    </>
  );
};

export default AIX;